import { ArrowNext, ArrowPrev } from '@/components/icons';
import { Image } from '@/components/ui/image';
import {
  Autoplay,
  EffectFade,
  Navigation,
  Swiper,
  SwiperSlide,
} from '@/components/ui/slider';
import { useBanner } from '@/framework/banner';
import { useIsRTL } from '@/lib/locals';
import { BannerQueryOptions } from '@/types';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Link from '../ui/link';
import BannerLoader from '../ui/loaders/banner-loader';
const ErrorMessage = dynamic(() => import('@/components/ui/error-message'));

const Banner: React.FC<BannerQueryOptions> = (params) => {
  const { banners, isLoading, error } = useBanner(params);
  const { t } = useTranslation('common');
  const { isRTL } = useIsRTL();

  if (isLoading) {
    return <BannerLoader />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  return (
    <div className="relative mt-0">
      <div className="-z-1 overflow-hidden">
        <div className="relative">
          <Swiper
            id="banner"
            loop={true}
            modules={[Navigation, Autoplay, EffectFade]}
            resizeObserver={true}
            allowTouchMove={false}
            slidesPerView={1}
            autoHeight={true}
            navigation={{
              nextEl: '.next',
              prevEl: '.prev',
            }}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            effect="fade"
            speed={1000}
            fadeEffect={{
              crossFade: true,
            }}
          >
            {banners?.map((banner, idx) => (
              <SwiperSlide key={idx}>
                <Link
                  href={banner.url ?? '/'}
                  target={banner.url_target ?? '_self'}
                >
                  <div className="relative h-[250px] w-full md:h-[350px]">
                    <Image
                      className="h-full w-full object-cover"
                      src={banner.image.original}
                      alt={banner.title ?? ''}
                      width={1503}
                      height={380}
                    />
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className="prev absolute top-2/4 z-10 -mt-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-white border-opacity-20 bg-white bg-opacity-20 text-heading shadow-xl backdrop-blur-lg transition-all duration-200 hover:bg-opacity-40 hover:shadow-2xl md:-mt-5 ltr:left-4 ltr:md:left-5 rtl:right-4 rtl:md:right-5"
            role="button"
          >
            <span className="sr-only">{t('text-previous')}</span>
            {isRTL ? (
              <ArrowNext width={18} height={18} className="text-gray-800" />
            ) : (
              <ArrowPrev width={18} height={18} className="text-gray-800" />
            )}
          </div>
          <div
            className="next absolute top-2/4 z-10 -mt-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-white border-opacity-20 bg-white bg-opacity-20 text-gray-100 text-heading shadow-xl backdrop-blur-lg transition-all duration-200 md:-mt-5 ltr:right-4 ltr:md:right-5 rtl:left-4 rtl:md:left-5"
            role="button"
          >
            <span className="sr-only">{t('text-next')}</span>
            {isRTL ? (
              <ArrowPrev width={18} height={18} className="text-gray-800" />
            ) : (
              <ArrowNext width={18} height={18} className="text-gray-800" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
